import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type { CapacityConflict } from './allocation-conflict';
import { describeConflict } from './allocation-conflict';
import { PLANNING_ERROR, PLANNING_ERROR_MESSAGE } from './errors';

/**
 * A exceção de capacidade (RN-045).
 *
 * O sistema **não** proíbe alocar acima da capacidade — obra tem mutirão, virada de
 * concretagem e sábado trabalhado. O que ele proíbe é fazer isso em silêncio. Passar do
 * limite exige duas coisas ao mesmo tempo, e nenhuma delas substitui a outra:
 *
 * 1. a permissão `labor.allocate.overcapacity`;
 * 2. uma justificativa escrita, que fica no registro e na auditoria.
 *
 * Quem não tem a permissão recebe recusa **explicativa** — não um botão escondido: a
 * pessoa precisa entender por que 12h não entraram e a quem pedir.
 */

/** Justificativa curta demais não é justificativa — é o campo preenchido para passar. */
export const MIN_JUSTIFICATION_LENGTH = 10;

export type OverCapacityOutcome =
  /** Cabe na capacidade: segue sem cerimônia. */
  | { readonly kind: 'WITHIN_CAPACITY' }
  /** Passa do limite, com permissão e justificativa — grava marcado e auditado. */
  | {
      readonly kind: 'OVERRIDE';
      readonly conflict: CapacityConflict;
      readonly justification: string;
    };

export interface OverCapacityInput {
  /** Ausente = não há excesso. */
  readonly conflict?: CapacityConflict;
  readonly hasPermission: boolean;
  readonly justification?: string;
  readonly resourceName?: string;
}

/**
 * Decide se a alocação passa.
 *
 * Função pura, sem sessão e sem repositório: recebe "há conflito?", "tem permissão?" e
 * "escreveu o porquê?". É chamada pelo caso de uso — a tela chama a mesma função só para
 * saber se deve abrir o diálogo de exceção.
 */
export function decideOverCapacity(
  input: OverCapacityInput,
): Result<OverCapacityOutcome, DomainError> {
  if (input.conflict === undefined) return ok({ kind: 'WITHIN_CAPACITY' });

  if (!input.hasPermission) {
    return err(
      domainError(
        PLANNING_ERROR.CAPACITY_OVERRIDE_FORBIDDEN,
        `${describeConflict(input.conflict, input.resourceName)} ${PLANNING_ERROR_MESSAGE[PLANNING_ERROR.CAPACITY_OVERRIDE_FORBIDDEN]!}`,
        { conflict: input.conflict },
      ),
    );
  }

  const justification = input.justification?.trim() ?? '';
  if (justification.length < MIN_JUSTIFICATION_LENGTH) {
    return err(
      domainError(
        PLANNING_ERROR.CAPACITY_OVERRIDE_NEEDS_REASON,
        justification.length === 0
          ? PLANNING_ERROR_MESSAGE[PLANNING_ERROR.CAPACITY_OVERRIDE_NEEDS_REASON]!
          : `A justificativa precisa de pelo menos ${String(MIN_JUSTIFICATION_LENGTH)} caracteres — descreva o motivo do excesso.`,
        { conflict: input.conflict, length: justification.length },
      ),
    );
  }

  return ok({ kind: 'OVERRIDE', conflict: input.conflict, justification });
}
